import { isLocale, locales, type Locale } from "./config";

/**
 * Language preference for the unprefixed homepage and the language hint. Search engines crawl
 * without Accept-Language, so they always resolve to Spanish (the x-default); only people who
 * prefer English or Portuguese are sent to /en or /pt, and only from "/".
 */
/** Cookie written when a visitor picks a language (switcher or hint): it always wins. */
export const LOCALE_COOKIE = "codifly_lang";
/** A year, in seconds. */
export const LOCALE_COOKIE_MAX_AGE = 31_536_000;

/** The first supported language of an Accept-Language header or navigator.languages, by quality. */
export function preferredLocale(accept: string | readonly string[] | null | undefined): Locale | null {
  if (!accept) return null;
  const entries = typeof accept === "string"
    ? accept.split(",").map((part, i) => {
      const [tag, ...params] = part.trim().split(";");
      const q = Number(params.map(p => p.trim()).find(p => p.startsWith("q="))?.slice(2) ?? 1);
      return { tag: tag.trim().toLowerCase(), q: Number.isFinite(q) ? q : 0, i };
    }).filter(e => e.tag && e.q > 0).sort((a, b) => b.q - a.q || a.i - b.i).map(e => e.tag)
    : accept.map(tag => tag.toLowerCase());
  for (const tag of entries) {
    const primary = tag.split("-")[0];
    if (isLocale(primary)) return primary;
  }
  return null;
}

/** The locale a cookie value names, if any. */
export const cookieLocale = (value: string | null | undefined): Locale | null => (value && isLocale(value) ? value : null);

/** Where "/" should send this visitor: null keeps the Spanish homepage (crawlers, Spanish, unknown). */
export function homeRedirect(cookie: string | null | undefined, acceptLanguage: string | null | undefined): Exclude<Locale, "es"> | null {
  const choice = cookieLocale(cookie) ?? preferredLocale(acceptLanguage);
  return choice && choice !== "es" ? choice : null;
}

export { locales };
